import { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { motion } from "framer-motion"
import { toast } from "sonner"
import { Pencil, Award } from "lucide-react"
import { useProfile } from "@/hooks/useProfile"
import { useCurrentBodyweight } from "@/hooks/useCurrentBodyweight"
import { useProgramBlock } from "@/hooks/useProgramBlock"
import {
  getRmHistory, recordRmMeasurement, upsertAthleteProfile,
  type RmHistoryRow,
} from "@/lib/supabase"
import {
  MOVEMENT_LABELS, RM_FIELD_BY_MOVEMENT, STRENGTH_MOVEMENTS,
  type StrengthMovement,
} from "@/lib/strengthStandards"
import { formatFr, toLocalISODate } from "@/lib/utils"
import { BottomNav } from "@/components/BottomNav"
import { ScreenHeader, SectionTitle, Card, Note, TONE } from "@/components/profile/ui"
import { EditSheet } from "@/components/profile/EditSheet"
import { NumberWheel } from "@/components/shared/NumberWheel"
import { HistoryChart, HistoryTimeline, type HistoryPoint } from "@/components/profile/HistoryView"

// Bornes inchangées (plus de 0, au plus 500 kg), pas de 0,5 kg comme avant.
const RM_WHEEL = { min: 0.5, max: 500, step: 0.5, fallback: 60 }

const PHASE_LABELS: Record<RmHistoryRow["phase"], string> = {
  initial: "Test initial",
  mid: "Test intermédiaire",
  final: "Test final",
  manual: "Relevé",
}

function isMovement(v: string | undefined): v is StrengthMovement {
  return !!v && (STRENGTH_MOVEMENTS as string[]).includes(v)
}

/**
 * Historique d'un mouvement : record actuel en grand, courbe des tests successifs, puis
 * la liste datée avec l'écart à chaque étape.
 *
 * C'est aussi l'endroit où l'on saisit un 1RM. Sur l'écran Profil, toucher une ligne de
 * record ouvre désormais cet écran au lieu d'ouvrir directement une modale de saisie :
 * la valeur se lit avant de se corriger, et la saisie garde son point d'entrée ici.
 */
export function RecordHistoryPage() {
  const { movement } = useParams<{ movement: string }>()
  const navigate = useNavigate()
  const { profile } = useProfile()
  const { bodyweightKg: currentBodyweight, loading: bodyweightLoading } = useCurrentBodyweight(profile?.raw.user_id)
  const block = useProgramBlock()

  const [history, setHistory] = useState<RmHistoryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<number>(RM_WHEEL.fallback)
  const [saving, setSaving] = useState(false)

  const valid = isMovement(movement)

  useEffect(() => {
    if (!valid) return
    let mounted = true
    getRmHistory(movement)
      .then((rows) => { if (mounted) setHistory(rows) })
      .finally(() => { if (mounted) setLoading(false) })
    return () => { mounted = false }
  }, [movement, valid])

  const points = useMemo(
    () => [...history].sort((a, b) => a.measured_on.localeCompare(b.measured_on)),
    [history]
  )
  // Vue partagée avec l'historique des mesures : la phase du test devient le libellé
  // d'étape, la valeur reste en kg.
  const historyPoints: HistoryPoint[] = useMemo(
    () => points.map((p) => ({
      id: p.id,
      date: p.measured_on,
      value: p.value,
      label: PHASE_LABELS[p.phase],
      // `is_max_effort` vaut null sur une saisie manuelle et sur tout ce qui précède le
      // parcours de test guidé : la question n'a pas été posée, il n'y a donc rien à
      // signaler. Seul un `false` explicite mérite la réserve.
      note: p.is_max_effort === false ? "Série arrêtée avant l'échec — valeur probablement sous-estimée" : undefined,
    })),
    [points]
  )

  const current = points.length > 0 ? points[points.length - 1] : null
  const first = points.length > 0 ? points[0] : null
  const totalGain = current && first && points.length > 1 ? current.value - first.value : null

  if (!valid) {
    return (
      <div className="min-h-screen bg-[#060a13]">
        <ScreenHeader title="Record" onBack={() => navigate("/dashboard/profile")} />
        <p className="px-5 mt-6 text-[13px] text-gray-500">Ce mouvement n'existe pas.</p>
        <BottomNav />
      </div>
    )
  }

  const label = MOVEMENT_LABELS[movement]
  // Dernière pesée (repli : poids d'inscription), pas `profile.weight` seul.
  const bodyweight = currentBodyweight ?? profile?.weight ?? null
  // Pas de rapport tant que la dernière pesée n'est pas lue : sinon il s'afficherait
  // un instant sur le poids d'inscription.
  const ratio = current && bodyweight && !bodyweightLoading ? current.value / bodyweight : null

  const openEdit = () => {
    // Record existant gardé tel quel (la molette le propose même hors grille) ; sans
    // record, la molette part du poids de corps actuel (dernière pesée).
    const existing = current ? Number(current.value) : NaN
    const body = Number(bodyweight)
    setDraft(
      Number.isFinite(existing) && existing > 0 ? existing
        : Number.isFinite(body) && body > 0 ? body
        : RM_WHEEL.fallback
    )
    setEditing(true)
  }

  const save = async () => {
    if (saving) return
    const n = draft
    setSaving(true)
    try {
      const measuredOn = toLocalISODate(new Date())
      // Les deux écritures vont ensemble : `athlete_profiles` porte la valeur courante
      // (c'est elle que lisent le radar et les cibles), `rm_history` porte la courbe.
      await upsertAthleteProfile({ [RM_FIELD_BY_MOVEMENT[movement]]: n, rm_test_date: measuredOn })
      await recordRmMeasurement({
        movement,
        valueKg: n,
        measuredOn,
        bodyweightKg: bodyweight,
        programmeId: block.programmeId,
        blockStartDate: block.blockStartDate,
      })
      setHistory(await getRmHistory(movement))
      toast.success(`${label} mis à jour`)
      setEditing(false)
    } catch (err) {
      toast.error("Erreur", { description: err instanceof Error ? err.message : undefined })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#060a13] pb-28 cursor-default">
      <ScreenHeader
        title={label}
        onBack={() => navigate("/dashboard/profile")}
        right={
          <button
            onClick={openEdit}
            aria-label="Saisir un record"
            className="w-9 h-9 rounded-[11px] grid place-items-center cursor-pointer"
            style={{ background: TONE.amber.bg, border: `1px solid ${TONE.amber.border}`, color: TONE.amber.fg }}
          >
            <Pencil className="w-[15px] h-[15px]" />
          </button>
        }
      />

      {/* RECORD ACTUEL */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="px-5 pt-3 pb-5 text-center">
        <p className="text-[10px] text-gray-600" style={{ letterSpacing: 2 }}>RECORD ACTUEL · 1RM</p>
        <p className="text-[52px] font-bold text-white leading-[1.05] mt-2 tracking-tight tnum">
          {current ? formatFr(current.value, current.value % 1 === 0 ? 0 : 1) : "—"}
          <span className="text-[20px] font-medium text-gray-500"> kg</span>
        </p>
        <p className="text-[13px] text-gray-400 mt-2 tnum">
          {totalGain != null && totalGain !== 0 && (
            <>
              <b style={{ color: totalGain > 0 ? "#4ade80" : "#f87171" }}>
                {totalGain > 0 ? "+" : "−"}{formatFr(Math.abs(totalGain), Math.abs(totalGain) % 1 === 0 ? 0 : 1)} kg
              </b>
              {" depuis le test initial"}
            </>
          )}
          {totalGain != null && totalGain !== 0 && ratio != null && " · "}
          {ratio != null && `${formatFr(ratio, 2)} × poids de corps`}
        </p>
        {movement === "traction_lestee" && (
          <p className="text-[11px] text-gray-600 mt-1.5">Charge totale : poids de corps + lest.</p>
        )}
      </motion.div>

      {/* COURBE */}
      {points.length >= 2 && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="px-4">
          <Card>
            <div className="px-2.5 pt-4 pb-2.5">
              <HistoryChart points={historyPoints} ariaLabel="Évolution du record" />
            </div>
          </Card>
        </motion.div>
      )}

      {/* LISTE DATÉE */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="px-4 mt-6">
        <SectionTitle label="Historique des tests" />
        <Card>
          {loading ? (
            <p className="text-[12px] text-gray-500 text-center py-6">Chargement…</p>
          ) : points.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <span className="w-11 h-11 rounded-[14px] grid place-items-center mx-auto mb-3"
                style={{ background: TONE.amber.bg, border: `1px solid ${TONE.amber.border}`, color: TONE.amber.fg }}>
                <Award className="w-5 h-5" />
              </span>
              <p className="text-[13px] text-gray-400">Aucun test enregistré</p>
              <button
                onClick={openEdit}
                className="mt-3 px-4 py-2 rounded-xl text-[13px] font-semibold cursor-pointer"
                style={{ background: TONE.amber.bg, border: `1px solid ${TONE.amber.border}`, color: TONE.amber.fg }}
              >
                Saisir mon record
              </button>
            </div>
          ) : (
            <HistoryTimeline
              points={historyPoints}
              formatValue={(v) => `${formatFr(v, v % 1 === 0 ? 0 : 1)} kg`}
            />
          )}
        </Card>
        <Note>
          Les valeurs mesurées en séance de test sont calculées à partir de ta série maximale,
          avec la formule de Brzycki. Celles que tu saisis toi-même sont reprises telles quelles.
        </Note>
      </motion.section>

      <EditSheet
        open={editing}
        title={label}
        onCancel={() => !saving && setEditing(false)}
        onSave={save}
        saving={saving}
        description={
          movement === "traction_lestee"
            ? "Saisis ta charge totale : ton poids de corps plus le lest."
            : "Saisis ton 1RM — le poids maximum sur une répétition."
        }
      >
        <div className="flex justify-center">
          <NumberWheel
            id="rm-record"
            label={label}
            labelHidden
            unit="kg"
            min={RM_WHEEL.min}
            max={RM_WHEEL.max}
            step={RM_WHEEL.step}
            value={draft}
            onChange={setDraft}
          />
        </div>
      </EditSheet>

      <BottomNav />
    </div>
  )
}
